import type { UpstreamAccountRecord } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Drawer } from '../../ui/index.ts';
import { AccountsCard } from '../AccountsCard.tsx';
import { FundingCard } from './FundingCard.tsx';
import { UpstreamChannelsCard } from './UpstreamChannelsCard.tsx';

/**
 * The three management surfaces the upstream detail page used to spread
 * across tab panes (epic #1327 PR-6). They are edits, not readings, so they
 * live behind drawers: the page now states supply readiness, and a drawer
 * opens exactly where an operator chose to change something.
 *
 * Each drawer mounts its card only while open. That is deliberate — the
 * cards own list queries plus modal state, and keeping them mounted would
 * both fetch for a closed surface and preserve half-finished forms across
 * open/close cycles.
 */

export type SupplyAccessDrawerId = 'accounts' | 'channels' | 'funding';

interface Props {
  readonly upstreamId: string;
  readonly open: SupplyAccessDrawerId | null;
  readonly onOpenChange: (next: SupplyAccessDrawerId | null) => void;
  /** Funding batches are registered against an account, so the list is shared. */
  readonly accounts: readonly UpstreamAccountRecord[];
  readonly focusAccountId: string | null;
  readonly focusKeyBucketId: string | null;
  readonly focusAddChannelId: string | null;
}

export function SupplyAccessDrawers({
  upstreamId,
  open,
  onOpenChange,
  accounts,
  focusAccountId,
  focusKeyBucketId,
  focusAddChannelId,
}: Props): JSX.Element {
  const t = useT();
  const close = (next: boolean): void => {
    if (!next) onOpenChange(null);
  };

  return (
    <>
      <Drawer
        open={open === 'accounts'}
        onOpenChange={close}
        title={t('upstream_supply_access.drawer.accounts.title')}
        description={t('upstream_supply_access.drawer.accounts.description')}
        width="xl"
        closeLabel={t('common.close')}
        testId="supply-access-accounts-drawer"
      >
        {open === 'accounts' ? (
          <AccountsCard
            upstreamId={upstreamId}
            focusAccountId={focusAccountId}
            focusKeyBucketId={focusKeyBucketId}
            focusAddChannelId={focusAddChannelId}
          />
        ) : null}
      </Drawer>

      <Drawer
        open={open === 'channels'}
        onOpenChange={close}
        title={t('upstream_supply_access.drawer.channels.title')}
        description={t('upstream_supply_access.drawer.channels.description')}
        width="xl"
        closeLabel={t('common.close')}
        testId="supply-access-channels-drawer"
      >
        {open === 'channels' ? <UpstreamChannelsCard upstreamId={upstreamId} /> : null}
      </Drawer>

      <Drawer
        open={open === 'funding'}
        onOpenChange={close}
        title={t('upstream_supply_access.drawer.funding.title')}
        width="xl"
        closeLabel={t('common.close')}
        testId="supply-access-funding-drawer"
      >
        {open === 'funding' ? <FundingCard upstreamId={upstreamId} accounts={accounts} /> : null}
      </Drawer>
    </>
  );
}
